import React, { useState, useEffect, useContext } from 'react';
import Chart from "react-apexcharts";
import { apiContext } from '../../../Api';

export default function BarChartQuartile() {
    const { axios } = useContext(apiContext);
    const [allData, setAllData] = useState([]);
    const [availableTypes, setAvailableTypes] = useState([]);
    const [selectedType, setSelectedType] = useState('Semua');
    const [chartData, setChartData] = useState({ series: [], categories: ['Q1', 'Q2', 'Q3', 'Q4', 'Lainnya'] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAndProcessData = async () => {
            try {
                const response = await axios.get('/data');
                const data = response.data || [];
                setAllData(data);

                // Extract unique types for the filter dropdown
                const types = new Set();
                data.forEach(item => {
                    const typeName = item?.jenis_publikasi?.nama || 'Tidak Terdefinisi';
                    types.add(String(typeName).trim());
                });
                
                // Sort types alphabetically for better UX
                setAvailableTypes(Array.from(types).sort());
            } catch (error) {
                console.error("Gagal mengambil data bar chart quartile:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAndProcessData();
    }, [axios]);

    // Process data whenever allData or selectedType changes
    useEffect(() => {
        if (allData.length === 0 && !loading) {
            setChartData({ series: [{ name: 'Jumlah Publikasi', data: [0, 0, 0, 0, 0] }], categories: ['Q1', 'Q2', 'Q3', 'Q4', 'Lainnya'] });
            return;
        }

        const counts = { 'Q1': 0, 'Q2': 0, 'Q3': 0, 'Q4': 0, 'Lainnya': 0 };

        allData.forEach(item => {
            const typeName = item?.jenis_publikasi?.nama || 'Tidak Terdefinisi';
            const cleanTypeName = String(typeName).trim();

            if (selectedType === 'Semua' || selectedType === cleanTypeName) {
                let q = item?.quartile || item?.detail_publikasi?.quartile;
                let qLabel = 'Lainnya';

                if (q === 1 || q === '1' || q === 'Q1') qLabel = 'Q1';
                else if (q === 2 || q === '2' || q === 'Q2') qLabel = 'Q2';
                else if (q === 3 || q === '3' || q === 'Q3') qLabel = 'Q3';
                else if (q === 4 || q === '4' || q === 'Q4') qLabel = 'Q4';
                else qLabel = 'Lainnya';

                counts[qLabel]++;
            }
        });

        const qLabels = ['Q1', 'Q2', 'Q3', 'Q4', 'Lainnya'];
        const dataValues = qLabels.map(label => counts[label]);

        setChartData({ 
            series: [{ name: 'Jumlah Publikasi', data: dataValues }], 
            categories: qLabels 
        });
    }, [allData, selectedType, loading]);

    const hasData = chartData.series.length > 0 && chartData.series[0].data.some(val => val > 0);

    const options = {
        chart: {
            type: 'bar',
            fontFamily: 'Inter, sans-serif',
            toolbar: {
                show: false
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                borderRadius: 4,
                columnWidth: '45%',
                distributed: true, // Memberikan warna berbeda untuk setiap bar Quartile
            },
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val === 0 ? '' : val; 
            },
            style: {
                fontSize: '11px',
                colors: ['#fff']
            }
        },
        stroke: {
            width: 0,
        },
        xaxis: {
            categories: chartData.categories,
            labels: {
                style: {
                    colors: '#64748b',
                    fontWeight: 600,
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#64748b',
                    fontWeight: 600,
                }
            }
        },
        // Premium color palette for Quartiles: Green(Q1), Blue(Q2), Orange(Q3), Red(Q4), Slate(Lainnya)
        colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#94a3b8'],
        fill: {
            opacity: 1
        },
        legend: {
            show: false // Distributed bars otomatis membuat legend yang sama persis dengan sumbu X, jadi kita sembunyikan agar rapi
        },
        tooltip: {
            theme: 'light',
            y: {
                formatter: function (val) {
                    return val + " Publikasi"
                }
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                chart: {
                    height: 320
                },
                plotOptions: {
                    bar: {
                        columnWidth: '70%',
                    }
                }
            }
        }]
    };

    return (
        <div className="flex h-full w-full flex-col justify-center transition-all duration-300">
            <div className="mb-2 flex flex-col items-center justify-between gap-3 sm:flex-row">
                <h3 className="text-sm font-extrabold text-slate-700 uppercase tracking-wider">
                    Distribusi Quartile
                </h3>
                {availableTypes.length > 0 && (
                    <select
                        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-brand-navy shadow-sm transition hover:border-brand-green focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green cursor-pointer"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                    >
                        <option value="Semua">Semua Jenis</option>
                        {availableTypes.map((type, idx) => (
                            <option key={idx} value={type}>{type}</option>
                        ))}
                    </select>
                )}
            </div>

            {loading ? (
                <div className="flex h-[320px] items-center justify-center">
                    <span className="text-sm font-medium text-slate-400 animate-pulse">Memuat data grafik...</span>
                </div>
            ) : (
                <div className="w-full mt-2">
                    {hasData ? (
                        <Chart
                            options={options}
                            series={chartData.series}
                            type="bar"
                            height={340}
                            width="100%"
                        />
                    ) : (
                        <div className="flex h-[340px] items-center justify-center text-sm font-medium text-slate-400 bg-slate-50/50 rounded-xl border border-dashed border-slate-200 mt-2">
                            Tidak ada data quartile untuk "{selectedType}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
